import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, Image, Archive, Trash2, Download, Eye, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  uploadProductLibraryFile,
  getProductLibraryFiles,
  downloadProductLibraryFile,
  getProductLibraryFilePreview,
  deleteProductLibraryFile
} from '@/api/api';

const ProductLibrary = ({ user }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    try {
      setLoading(true);
      const data = await getProductLibraryFiles();
      setFiles(data);
    } catch (error) {
      toast.error('Dosyalar yüklenemedi');
      console.error('Error loading files:', error);
    } finally {
      setLoading(false);
    }
  };

  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    
    // Dosya tipi kontrolü
    const allowedTypes = ['application/zip', 'application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Sadece ZIP, PDF, JPG ve PNG dosyaları yüklenebilir');
      return;
    }

    // Boyut kontrolü
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Dosya boyutu maksimum 50MB olabilir');
      return;
    }

    try {
      setUploading(true);
      await uploadProductLibraryFile(file);
      toast.success('Dosya başarıyla yüklendi');
      loadFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dosya yüklenemedi');
      console.error('Error uploading file:', error);
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/zip': ['.zip'],
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    maxFiles: 1,
    disabled: uploading
  });

  const handleDownload = async (file) => {
    try {
      const response = await downloadProductLibraryFile(file.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Dosya indiriliyor');
    } catch (error) {
      toast.error('Dosya indirilemedi');
      console.error('Error downloading file:', error);
    }
  };

  const handlePreview = (file) => {
    if (file.file_type === 'zip') {
      toast.info('ZIP dosyaları önizlenemiyor, indirip açabilirsiniz');
      return;
    }
    setPreviewFile(file);
    setShowPreviewDialog(true);
  };

  const handleDelete = async (file) => {
    // Sadece admin veya yükleyen kişi silebilir
    if (user?.role !== 'admin' && file.uploaded_by !== user?.username) {
      toast.error('Bu dosyayı silme yetkiniz yok');
      return;
    }

    if (!window.confirm(`"${file.original_filename}" dosyasını silmek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      await deleteProductLibraryFile(file.id);
      toast.success('Dosya silindi');
      loadFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dosya silinemedi');
      console.error('Error deleting file:', error);
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'pdf':
        return <FileText className="w-12 h-12 text-red-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
        return <Image className="w-12 h-12 text-blue-500" />;
      case 'zip':
        return <Archive className="w-12 h-12 text-orange-500" />;
      default:
        return <FileText className="w-12 h-12 text-gray-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <Archive className="w-8 h-8 mr-3 text-blue-600" />
          Ürün Kütüphanesi
        </h1>
        <p className="text-gray-600 mt-1">Ürün katalogları, teknik dökümanlar ve görseller</p>
      </div>

      {/* Upload Area */}
      <div
        {...getRootProps()}
        className={`mb-6 border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
        } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        {uploading ? (
          <div>
            <p className="text-lg font-semibold text-gray-700">Yükleniyor...</p>
            <p className="text-sm text-gray-500 mt-1">Lütfen bekleyin</p>
          </div>
        ) : isDragActive ? (
          <div>
            <p className="text-lg font-semibold text-blue-600">Dosyayı buraya bırakın</p>
          </div>
        ) : (
          <div>
            <p className="text-lg font-semibold text-gray-700">
              Dosya yüklemek için tıklayın veya sürükleyin
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Desteklenen formatlar: ZIP, PDF, JPG, PNG (Maks. 50MB)
            </p>
          </div>
        )}
      </div>

      {/* Files Grid */}
      {files.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Archive className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Henüz Dosya Yok</h3>
          <p className="text-gray-600">
            Yukarıdaki alandan dosya yükleyerek kütüphaneyi oluşturmaya başlayın
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {files.map((file) => {
            const canDelete = user?.role === 'admin' || file.uploaded_by === user?.username;
            
            return (
              <div
                key={file.id}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-4 border border-gray-200"
              >
                <div className="flex flex-col items-center mb-3">
                  {getFileIcon(file.file_type)}
                  <h3 className="text-sm font-semibold text-gray-800 mt-2 text-center line-clamp-2">
                    {file.original_filename}
                  </h3>
                </div>

                <div className="text-xs text-gray-500 space-y-1 mb-3">
                  <div>Boyut: {formatFileSize(file.file_size)}</div>
                  <div>Yükleyen: {file.uploaded_by}</div>
                  <div>
                    Tarih: {new Date(file.uploaded_at).toLocaleDateString('tr-TR', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </div>
                </div>

                <div className="flex gap-2">
                  {file.file_type !== 'zip' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePreview(file)}
                      className="flex-1"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Önizle
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(file)}
                    className="flex-1"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    İndir
                  </Button>
                  {canDelete && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(file)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{previewFile?.original_filename}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPreviewDialog(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {previewFile && (
              <>
                {previewFile.file_type === 'pdf' ? (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                      <FileText className="w-16 h-16 mx-auto mb-3 text-blue-600" />
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">
                        {previewFile.original_filename}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Dosya boyutu: {formatFileSize(previewFile.file_size)}
                      </p>
                      <div className="flex justify-center gap-3">
                        <Button
                          onClick={() => {
                            window.open(getProductLibraryFilePreview(previewFile.id), '_blank');
                          }}
                          className="progo-bg hover:bg-blue-700"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Yeni Sekmede Aç
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleDownload(previewFile)}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          İndir
                        </Button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 text-center">
                      PDF dosyası için yeni sekmede açma veya indirme önerilir
                    </div>
                  </div>
                ) : (
                  <img
                    src={getProductLibraryFilePreview(previewFile.id)}
                    alt={previewFile.original_filename}
                    className="w-full h-auto rounded"
                  />
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductLibrary;
